import React, { createContext, useCallback, useContext, useState } from "react";

/**
 * Id do container do drawer da Sidebar (modo mobile).
 * Compartilhado entre o botão hamburguer (AppHeader, via `aria-controls`) e o
 * próprio drawer (Sidebar, via `id`), garantindo a associação para leitores de tela.
 */
export const SIDEBAR_DRAWER_ID = "app-sidebar-drawer";

/**
 * SidebarDrawerContext
 *
 * Compartilha o estado do drawer da Sidebar entre o AppHeader (botão hamburger)
 * e as páginas que possuem Sidebar (Agenda, Customers, Reports).
 *
 * Uso:
 *   - AppLayout provê o contexto via SidebarDrawerProvider.
 *   - AppHeader lê `onMenuClick` do contexto para exibir o hamburger.
 *   - As páginas chamam `useSidebarDrawer()` para obter `drawerOpen` e `closeDrawer`.
 */

const SidebarDrawerContext = createContext({
  drawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
  hasSidebar: false,
  setHasSidebar: () => {},
});

export function SidebarDrawerProvider({ children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hasSidebar, setHasSidebar] = useState(false);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  return (
    <SidebarDrawerContext.Provider
      value={{ drawerOpen, openDrawer, closeDrawer, hasSidebar, setHasSidebar }}
    >
      {children}
    </SidebarDrawerContext.Provider>
  );
}

/**
 * Hook para consumir o contexto do drawer da Sidebar.
 * Deve ser usado dentro de um SidebarDrawerProvider.
 */
export function useSidebarDrawer() {
  return useContext(SidebarDrawerContext);
}
